import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from './AuthProvider';
import { User, LogOut, Settings, Copy, Plus, Check, Mail, X } from 'lucide-react';
import { SettingsModal } from '@/components/SettingsModal';
import { TeamSetupModal } from '@/components/team/TeamSetupModal';
import { useToast } from '@/hooks/use-toast';
import { useTeams, useCurrentTeam } from '@/hooks/useTeams';
import { usePendingInvitations } from '@/hooks/usePendingInvitations';
import { supabase } from '@/integrations/supabase/client';
export const UserMenu = () => {
  const {
    user,
    signOut
  } = useAuth();
  const {
    teams,
    loading: teamsLoading,
    refetch: refetchTeams
  } = useTeams();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [teamSetupOpen, setTeamSetupOpen] = useState(false);
  const [partnerSlug, setPartnerSlug] = useState<string | null>(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);
  const [recentTeamIds, setRecentTeamIds] = useState<string[]>([]);
  const {
    currentTeamId,
    setCurrentTeamId
  } = useCurrentTeam();
  const [logoErrors, setLogoErrors] = useState<Set<string>>(new Set());
  
  const {
    toast
  } = useToast();
  
  const {
    invitations,
    acceptInvitation,
    declineInvitation,
    refetch: refetchInvitations
  } = usePendingInvitations();
  useEffect(() => {
    const fetchPartnerData = async () => {
      if (!user) return;
      const {
        data
      } = await supabase.from('user_profiles').select('partner_slug, profile_picture_url, recent_team_ids').eq('user_id', user.id).maybeSingle();
      setPartnerSlug(data?.partner_slug || null);
      setProfilePictureUrl(data?.profile_picture_url || null);
      setRecentTeamIds(data?.recent_team_ids || []);
    };
    fetchPartnerData();
  }, [user]);
  const handleCopyInviteLink = async () => {
    if (!partnerSlug) {
      toast({
        title: 'Setup required',
        description: 'Please complete your profile settings first to get your invite link.',
        variant: 'destructive'
      });
      return;
    }
    const inviteUrl = `${window.location.origin}/new/${partnerSlug}`;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast({
        title: 'Invite link copied!',
        description: 'Share this link with clients to get business valuations.',
        duration: 5000
      });
    } catch (error) {
      toast({
        title: 'Failed to copy',
        description: 'Please try again.',
        variant: 'destructive'
      });
    }
  };
  const handleTeamSwitch = async (teamId: string) => {
    console.log('UserMenu: Switching to team:', teamId);
    setCurrentTeamId(teamId);
    
    // Navigate to main dashboard
    navigate('/');
    
    // Update recent team IDs in the database
    if (user) {
      try {
        await supabase.rpc('update_recent_team_ids', {
          p_user_id: user.id,
          p_team_id: teamId
        });
        
        // Update local state
        const updatedRecentIds = [teamId, ...recentTeamIds.filter(id => id !== teamId)].slice(0, 5);
        setRecentTeamIds(updatedRecentIds);
      } catch (error) {
        console.error('Error updating recent team IDs:', error);
      }
    }
  };
  const handleTeamCreated = (newTeamId?: string) => {
    setTeamSetupOpen(false);
    if (newTeamId) setCurrentTeamId(newTeamId);
  };

  const handleAcceptInvite = async (invitationId: string, teamName: string) => {
    const success = await acceptInvitation(invitationId);
    if (success) {
      toast({
        title: 'Invitation accepted!',
        description: `Welcome to ${teamName}`,
      });
      // Refresh teams and reload auth state  
      await refetchTeams();
    } else {
      toast({
        title: 'Failed to accept invitation',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleDeclineInvite = async (invitationId: string, teamName: string) => {
    const success = await declineInvitation(invitationId);
    if (success) {
      toast({
        title: 'Invitation declined',
        description: `Declined invitation to ${teamName}`,
      });
    } else {
      toast({
        title: 'Failed to decline invitation',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };
  if (!user) {
    return null;
  }
  const getCurrentTeam = () => {
    return teams.find(t => t.team_id === currentTeamId);
  };
  const getUserDisplayName = () => {
    return user?.user_metadata?.name || user?.email?.split('@')[0] || 'User';
  };
  const getInitials = (email: string) => {
    return email.charAt(0).toUpperCase();
  };
  const getCompanyLogoUrl = (companyLogoUrl: string | null) => {
    if (!companyLogoUrl) return null;
    return companyLogoUrl;
  };

  const getDiceBearAvatarUrl = (teamId: string) => {
    return `https://api.dicebear.com/9.x/glass/svg?seed=${teamId}&size=24&backgroundType=gradientLinear&radius=4`;
  };
  const handleLogoError = (teamId: string) => {
    setLogoErrors(prev => new Set([...prev, teamId]));
  };
  const handleLogoLoad = (teamId: string, event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    // Check if the logo is likely empty or too small
    if (img.naturalWidth <= 16 || img.naturalHeight <= 16) {
      setLogoErrors(prev => new Set([...prev, teamId]));
    }
  };
  
  // Sort teams by recent team IDs, then by joined_at for remaining teams
  const getSortedTeams = () => {
    const recentTeams = recentTeamIds
      .map(id => teams.find(team => team.team_id === id))
      .filter(Boolean) as typeof teams;
    
    const remainingTeams = teams
      .filter(team => !recentTeamIds.includes(team.team_id))
      .sort((a, b) => new Date(b.joined_at).getTime() - new Date(a.joined_at).getTime());
    
    return [...recentTeams, ...remainingTeams];
  };

  const sortedTeams = getSortedTeams();

  return <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <Avatar className="h-8 w-8">
            {profilePictureUrl && <AvatarImage src={profilePictureUrl} alt="Profile picture" />}
            <AvatarFallback className="bg-primary text-primary-foreground">
              {getInitials(user.email || '')}
            </AvatarFallback>
          </Avatar>
          {/* Pending invitations badge */}
          {invitations.length > 0 && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
              {invitations.length}
            </div>
          )}
          {/* Team logo overlay */}
          {getCurrentTeam() && (
            <div className="absolute -bottom-0.5 -left-0.5 w-4 h-4 rounded-full border-2 border-background overflow-hidden bg-background">
              {(() => {
                const currentTeam = getCurrentTeam();
                const logoUrl = getCompanyLogoUrl(currentTeam?.teams.company_logo_url);
                const hasLogo = logoUrl && !logoErrors.has(currentTeam?.team_id || '');
                
                return hasLogo ? (
                  <img 
                    src={logoUrl} 
                    alt={`${currentTeam?.teams.name} logo`} 
                    className="w-full h-full object-cover"
                    onError={() => handleLogoError(currentTeam?.team_id || '')}
                    onLoad={e => handleLogoLoad(currentTeam?.team_id || '', e)}
                  />
                ) : (
                  <img 
                    src={getDiceBearAvatarUrl(currentTeam?.team_id || '')} 
                    alt={`${currentTeam?.teams.name} avatar`} 
                    className="w-full h-full object-cover"
                  />
                );
              })()}
            </div>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-60 bg-card border shadow-sm z-50 rounded-lg" align="end" forceMount>
        {/* Pending Invitations Section */}
        {invitations.length > 0 && (
          <>
            <div className="p-1.5 px-0 py-0">
              <DropdownMenuLabel className="px-1.5 py-1 text-xs font-medium text-muted-foreground">
                Team Invitations ({invitations.length})
              </DropdownMenuLabel>
              {invitations.map((invitation) => (
                <div key={invitation.id} className="px-1.5 py-1.5 rounded-md border bg-muted/50 mx-1.5 mb-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Mail className="w-3 h-3 text-muted-foreground" />
                        <span className="text-xs font-medium text-foreground truncate">
                          {invitation.teams.name}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Role: <span className="capitalize">{invitation.role}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1 mt-2">
                    <Button
                      size="sm"
                      className="h-6 px-2 text-xs bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => handleAcceptInvite(invitation.id, invitation.teams.name)}
                    >
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-xs"
                      onClick={() => handleDeclineInvite(invitation.id, invitation.teams.name)}
                    >
                      Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Teams Section */}
        <div className="p-1.5 px-0 py-0">
          {/* Teams list with scroll if more than 5 */}
          <div className={`${sortedTeams.length > 5 ? 'max-h-[200px] overflow-y-auto' : ''}`}>
            {sortedTeams.map(team => {
              const logoUrl = getCompanyLogoUrl(team.teams.company_logo_url);
              const hasLogo = logoUrl && !logoErrors.has(team.team_id);
              return <DropdownMenuItem key={team.team_id} onClick={() => handleTeamSwitch(team.team_id)} className="px-1.5 py-1.5 rounded-md cursor-pointer">
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2.5">
                    <div className="w-6 h-6 rounded border flex items-center justify-center overflow-hidden bg-muted">
                      {hasLogo ? <img src={logoUrl} alt={`${team.teams.name} logo`} className="w-full h-full object-cover" onError={() => handleLogoError(team.team_id)} onLoad={e => handleLogoLoad(team.team_id, e)} /> : <img src={getDiceBearAvatarUrl(team.team_id)} alt={`${team.teams.name} avatar`} className="w-full h-full object-cover" />}
                    </div>
                    <span className="text-sm text-foreground flex-1 truncate">
                      {team.teams.name}
                    </span>
                  </div>
                  {currentTeamId === team.team_id && <Check className="w-4 h-4 text-green-600" />}
                </div>
              </DropdownMenuItem>;
            })}
          </div>
          
          <DropdownMenuItem onClick={() => setTeamSetupOpen(true)} className="px-1.5 py-1.5 rounded-md cursor-pointer">
            <div className="flex items-center gap-2.5 w-full">
              <div className="w-5.5 h-5.5 rounded bg-primary/10 flex items-center justify-center p-1">
                <Plus className="w-3.5 h-3.5 text-primary" />
              </div>
              <span className="text-sm text-foreground">Create New Team</span>
            </div>
          </DropdownMenuItem>
        </div>

        <DropdownMenuSeparator className="border-[#F0F0F0]" />

        {/* Settings Section */}
        <div className="p-1.5 px-0 py-0">
          <DropdownMenuItem onClick={() => navigate('/settings')} className="px-1.5 py-1.5 rounded-md cursor-pointer">
            <div className="flex items-center gap-2 w-full">
              <Settings className="w-4 h-4 text-foreground" />
              <span className="text-sm text-foreground">Account Settings</span>
            </div>
          </DropdownMenuItem>
        </div>

        <DropdownMenuSeparator className="border-[#F0F0F0]" />

        <div className="p-1.5 px-0 py-0">
          <DropdownMenuItem onClick={signOut} className="px-1.5 py-1.5 rounded-md cursor-pointer">
            <div className="flex items-center gap-2 w-full">
              <LogOut className="w-4 h-4 text-destructive" />
              <span className="text-sm text-destructive">Sign out</span>
            </div>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
      
      <SettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} />
      <TeamSetupModal open={teamSetupOpen} onOpenChange={setTeamSetupOpen} onTeamCreated={handleTeamCreated} />
    </DropdownMenu>;
};